<script setup>
import { ref, computed } from 'vue';
import { Card } from 'primevue';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import ANButton from '@/components/common/AnButton.vue';
import Chart from 'primevue/chart';
import licPlanConfig from '@/lib/licPlanConfig.json';
import {
  compareLicVsMutualFund,
  formatINR,
  formatPct,
  getDefaultFabPer1000,
  getDefaultSrbPer1000,
  getPlan,
} from '@/utils/comparisonEngine.js';

// Example scenarios from config
const examples = licPlanConfig.examples || [];

// Form data
const formData = ref({
  inflationPct: licPlanConfig.assumptions.inflationDefaultPct || 6,
  exampleIndex: -1,
  mf: {
    investmentMode: 'monthly',
    monthlyInvestment: 5000,
    annualInvestment: 60000,
    years: 20,
    expectedReturnPct: 12,
    stepUpPct: 0,
  },
  lic: {
    planKey: 'new_endowment',
    basicSumAssured: 1000000,
    annualPremium: 60000,
    policyTermYears: 20,
    premiumPayingTermYears: 20,
    annualBonusMode: 'historical_average',
    manualAnnualBonusPer1000: 40.78,
    applyHighSaStepUp: true,
    fabMode: 'default_term_ladder',
    manualFabPer1000: 70,
    isPaidUp: false,
  },
});

const result = ref(null);
const chartData = ref({});
const chartOptions = ref({});

// Dropdown options
const planOptions = computed(() =>
  Object.entries(licPlanConfig.plans).map(([value, plan]) => ({
    label: plan.label,
    value,
  }))
);

const exampleOptions = computed(() => [
  { label: 'Custom', value: -1 },
  ...examples.map((example, idx) => ({ label: example.title, value: idx })),
]);

const investmentModeOptions = [
  { label: 'Monthly SIP', value: 'monthly' },
  { label: 'Annual Investment', value: 'annual' },
];

const annualBonusModeOptions = [
  { label: 'Historical Average', value: 'historical_average' },
  { label: 'Manual Override', value: 'manual' },
];

const fabModeOptions = [
  { label: 'Use Default FAB', value: 'default_term_ladder' },
  { label: 'Manual Override', value: 'manual' },
];

const yesNoOptions = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const currentPlan = computed(() => getPlan(formData.value.lic.planKey));

// Computed properties
const isValidInput = computed(() => {
  const mf = formData.value.mf;
  const lic = formData.value.lic;
  const mfAmountValid = mf.investmentMode === 'monthly' ? mf.monthlyInvestment > 0 : mf.annualInvestment > 0;
  return (
    mfAmountValid &&
    mf.years > 0 &&
    mf.expectedReturnPct >= 0 &&
    lic.basicSumAssured > 0 &&
    lic.policyTermYears > 0 &&
    lic.premiumPayingTermYears > 0
  );
});

// Sync LIC defaults when plan / term / SA changes
const syncLicDefaults = () => {
  formData.value.lic.manualAnnualBonusPer1000 = getDefaultSrbPer1000({
    planKey: formData.value.lic.planKey,
    policyTermYears: formData.value.lic.policyTermYears,
    basicSumAssured: formData.value.lic.basicSumAssured,
    applyHighSaStepUp: formData.value.lic.applyHighSaStepUp,
  });

  formData.value.lic.manualFabPer1000 = getDefaultFabPer1000({
    planKey: formData.value.lic.planKey,
    policyTermYears: formData.value.lic.policyTermYears,
  });
};

// Apply preset example scenario
const applyExample = () => {
  const idx = Number(formData.value.exampleIndex);
  if (Number.isNaN(idx) || idx < 0 || !examples[idx]) return;

  const ex = examples[idx];
  formData.value.mf.investmentMode = ex.mutualFund.monthlyInvestment ? 'monthly' : 'annual';
  formData.value.mf.monthlyInvestment = ex.mutualFund.monthlyInvestment || 0;
  formData.value.mf.annualInvestment = ex.mutualFund.annualInvestment || 0;
  formData.value.mf.years = ex.mutualFund.years;
  formData.value.mf.expectedReturnPct = ex.mutualFund.expectedReturnPct;
  formData.value.mf.stepUpPct = ex.mutualFund.stepUpPct || 0;

  formData.value.lic.planKey = ex.lic.planKey;
  formData.value.lic.basicSumAssured = ex.lic.basicSumAssured;
  formData.value.lic.annualPremium = ex.lic.annualPremium;
  formData.value.lic.policyTermYears = ex.lic.policyTermYears;
  formData.value.lic.premiumPayingTermYears = ex.lic.premiumPayingTermYears;
  formData.value.lic.isPaidUp = false;
  formData.value.lic.annualBonusMode = 'historical_average';
  formData.value.lic.fabMode = 'default_term_ladder';
  syncLicDefaults();
  calculate();
};

// Calculate comparison
const calculate = () => {
  if (!isValidInput.value) return;

  const lic = formData.value.lic;
  result.value = compareLicVsMutualFund({
    inflationPct: formData.value.inflationPct,
    licInput: {
      ...lic,
      manualAnnualBonusPer1000: lic.annualBonusMode === 'manual' ? lic.manualAnnualBonusPer1000 : null,
      manualFabPer1000: lic.fabMode === 'manual' ? lic.manualFabPer1000 : null,
    },
    mfInput: {
      ...formData.value.mf,
    },
  });

  generateChart();

  // Scroll to results section after calculation
  setTimeout(() => {
    const resultsElement = document.querySelector('.result-card');
    if (resultsElement) {
      resultsElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, 100);
};

// Generate comparison chart data
const generateChart = () => {
  if (!result.value) return;

  const lic = result.value.lic;
  const mf = result.value.mutualFund;

  chartData.value = {
    labels: ['Total Invested', 'Final / Total Value', 'Inflation-adjusted Value'],
    datasets: [
      {
        label: 'LIC Plan',
        data: [lic.totalPremiumPaid, lic.totalPolicyValueIncludingEarlierPayouts, lic.inflationAdjustedTotalValue],
        backgroundColor: 'rgba(15, 118, 110, 0.7)',
        borderColor: 'rgba(15, 118, 110, 1)',
        borderWidth: 2,
      },
      {
        label: 'Mutual Fund',
        data: [mf.totalInvested, mf.finalValue, mf.inflationAdjustedFinalValue],
        backgroundColor: 'rgba(37, 99, 235, 0.7)',
        borderColor: 'rgba(37, 99, 235, 1)',
        borderWidth: 2,
      },
    ],
  };

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'LIC Plan vs Mutual Fund Comparison',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return '₹' + (value / 100000).toFixed(1) + 'L';
          },
        },
      },
    },
  };
};

// Initialize
syncLicDefaults();
calculate();
</script>

<template>
  <div class="mf-lic-comparison-calculator">
    <div class="calculator-grid">
      <!-- Mutual Fund Inputs -->
      <Card class="input-card">
        <template #title>
          <h3>Mutual Fund vs LIC Inputs</h3>
        </template>
        <template #content>
          <div class="form-fields">
            <!-- Global Assumptions -->
            <div class="section-title">Global Assumptions</div>

            <div class="field">
              <label for="example">Example Scenario</label>
              <Select
                id="example"
                v-model="formData.exampleIndex"
                :options="exampleOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Select a scenario"
                class="input-field"
                @change="applyExample"
              />
              <small class="help-text">Pick a preset or customise the inputs below</small>
            </div>

            <div class="field">
              <label for="inflation">Inflation (%)</label>
              <InputNumber
                id="inflation"
                v-model="formData.inflationPct"
                :min="0"
                :max="20"
                :step="0.1"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Mutual Fund -->
            <div class="section-title">Mutual Fund Information</div>

            <div class="field">
              <label for="mfMode">Investment Mode</label>
              <Select
                id="mfMode"
                v-model="formData.mf.investmentMode"
                :options="investmentModeOptions"
                optionLabel="label"
                optionValue="value"
                class="input-field"
              />
            </div>

            <div v-if="formData.mf.investmentMode === 'monthly'" class="field">
              <label for="mfMonthly">Monthly SIP (₹)</label>
              <InputNumber
                id="mfMonthly"
                v-model="formData.mf.monthlyInvestment"
                :min="0"
                :max="10000000"
                :step="500"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <div v-else class="field">
              <label for="mfAnnual">Annual Investment (₹)</label>
              <InputNumber
                id="mfAnnual"
                v-model="formData.mf.annualInvestment"
                :min="0"
                :max="100000000"
                :step="1000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="mfYears">Years</label>
              <InputNumber
                id="mfYears"
                v-model="formData.mf.years"
                :min="1"
                :max="50"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="mfReturn">Expected Return (%)</label>
              <InputNumber
                id="mfReturn"
                v-model="formData.mf.expectedReturnPct"
                :min="0"
                :max="30"
                :step="0.1"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="mfStepUp">Annual Step-up (%)</label>
              <InputNumber
                id="mfStepUp"
                v-model="formData.mf.stepUpPct"
                :min="0"
                :max="30"
                :step="0.1"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>
          </div>
        </template>
      </Card>

      <!-- LIC Inputs -->
      <Card class="input-card">
        <template #title>
          <h3>LIC Plan Information</h3>
        </template>
        <template #content>
          <div class="form-fields">
            <div class="field">
              <label for="planKey">Plan Family</label>
              <Select
                id="planKey"
                v-model="formData.lic.planKey"
                :options="planOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Select Plan"
                class="input-field"
                @change="syncLicDefaults"
              />
            </div>

            <div class="field">
              <label for="sumAssured">Basic Sum Assured (₹)</label>
              <InputNumber
                id="sumAssured"
                v-model="formData.lic.basicSumAssured"
                :min="100000"
                :max="100000000"
                :step="50000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
                @input="syncLicDefaults"
              />
            </div>

            <div class="field">
              <label for="annualPremium">Annual Premium (₹)</label>
              <InputNumber
                id="annualPremium"
                v-model="formData.lic.annualPremium"
                :min="0"
                :max="10000000"
                :step="1000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="policyTerm">Policy Term (Years)</label>
              <InputNumber
                id="policyTerm"
                v-model="formData.lic.policyTermYears"
                :min="5"
                :max="50"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
                @input="syncLicDefaults"
              />
            </div>

            <div class="field">
              <label for="pptYears">Premium Paying Term (Years)</label>
              <InputNumber
                id="pptYears"
                v-model="formData.lic.premiumPayingTermYears"
                :min="1"
                :max="50"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="bonusMode">Annual Bonus Mode</label>
              <Select
                id="bonusMode"
                v-model="formData.lic.annualBonusMode"
                :options="annualBonusModeOptions"
                optionLabel="label"
                optionValue="value"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="manualSrb">Manual SRB per ₹1,000</label>
              <InputNumber
                id="manualSrb"
                v-model="formData.lic.manualAnnualBonusPer1000"
                :min="0"
                :max="200"
                :step="0.1"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                class="input-field"
                :disabled="formData.lic.annualBonusMode !== 'manual'"
              />
              <small class="help-text">Editable only in Manual Override mode</small>
            </div>

            <div class="field">
              <label for="fabMode">FAB Mode</label>
              <Select
                id="fabMode"
                v-model="formData.lic.fabMode"
                :options="fabModeOptions"
                optionLabel="label"
                optionValue="value"
                class="input-field"
              />
            </div>

            <div class="field">
              <label for="manualFab">Manual FAB per ₹1,000</label>
              <InputNumber
                id="manualFab"
                v-model="formData.lic.manualFabPer1000"
                :min="0"
                :max="2000"
                :step="0.1"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                class="input-field"
                :disabled="formData.lic.fabMode !== 'manual'"
              />
              <small class="help-text">Editable only in Manual Override mode</small>
            </div>

            <div class="field">
              <label for="highSa">Apply high SA step-up</label>
              <Select
                id="highSa"
                v-model="formData.lic.applyHighSaStepUp"
                :options="yesNoOptions"
                optionLabel="label"
                optionValue="value"
                class="input-field"
                @change="syncLicDefaults"
              />
            </div>

            <div class="field">
              <label for="paidUp">
                Policy paid-up / stopped?
                <i
                  class="pi pi-info-circle info-icon"
                  v-tooltip.top="{
                    value:
                      'A policy becomes \'paid-up\' if you stop paying premiums before completing the Premium Paying Term (after it acquires a minimum value). Future annual bonuses (SRB) stop accruing and the Final Additional Bonus (FAB) is usually NOT payable, which can materially reduce the policy\'s maturity value. Select \'Yes\' to model a stopped-premium scenario.',
                    class: 'short-width bottom-arrow',
                  }"
                ></i>
              </label>
              <Select
                id="paidUp"
                v-model="formData.lic.isPaidUp"
                :options="yesNoOptions"
                optionLabel="label"
                optionValue="value"
                class="input-field"
              />
            </div>

            <!-- Calculate Button -->
            <ANButton
              label="Calculate"
              severity="primary"
              size="large"
              @click="calculate"
              :disabled="!isValidInput"
              class="calculate-btn"
            />
          </div>
        </template>
      </Card>
    </div>

    <!-- Results -->
    <div v-if="result" class="results-grid">
      <!-- LIC Output -->
      <Card class="result-card result-lic">
        <template #title>
          <h3>LIC Output</h3>
        </template>
        <template #content>
          <div class="metrics">
            <div class="metric"><span>Total Premium Paid</span><strong>{{ formatINR(result.lic.totalPremiumPaid) }}</strong></div>
            <div class="metric"><span>Maturity Base</span><strong>{{ formatINR(result.lic.maturityBase) }}</strong></div>
            <div class="metric"><span>SRB Used (per ₹1,000)</span><strong>{{ result.lic.annualBonusPer1000 }}</strong></div>
            <div class="metric"><span>Vested Bonus</span><strong>{{ formatINR(result.lic.vestedBonus) }}</strong></div>
            <div class="metric"><span>FAB Used (per ₹1,000)</span><strong>{{ result.lic.fabPer1000 }}</strong></div>
            <div class="metric"><span>Final Additional Bonus</span><strong>{{ formatINR(result.lic.fab) }}</strong></div>
            <div class="metric"><span>Maturity Only</span><strong>{{ formatINR(result.lic.maturityOnly) }}</strong></div>
            <div class="metric"><span>Earlier Survival Benefits</span><strong>{{ formatINR(result.lic.survivalBenefits.total) }}</strong></div>
            <div class="metric highlight"><span>Total Policy Value</span><strong>{{ formatINR(result.lic.totalPolicyValueIncludingEarlierPayouts) }}</strong></div>
            <div class="metric"><span>Implied IRR</span><strong>{{ formatPct(result.lic.impliedIrrPct) }}</strong></div>
            <div class="metric"><span>Inflation-adjusted Value</span><strong>{{ formatINR(result.lic.inflationAdjustedTotalValue) }}</strong></div>
          </div>
        </template>
      </Card>

      <!-- Mutual Fund Output -->
      <Card class="result-card result-mf">
        <template #title>
          <h3>Mutual Fund Output</h3>
        </template>
        <template #content>
          <div class="metrics">
            <div class="metric"><span>Total Invested</span><strong>{{ formatINR(result.mutualFund.totalInvested) }}</strong></div>
            <div class="metric highlight"><span>Final Value</span><strong>{{ formatINR(result.mutualFund.finalValue) }}</strong></div>
            <div class="metric"><span>Wealth Gain</span><strong>{{ formatINR(result.mutualFund.wealthGain) }}</strong></div>
            <div class="metric"><span>Expected Return</span><strong>{{ formatPct(result.mutualFund.impliedIrrPct) }}</strong></div>
            <div class="metric"><span>Real Return</span><strong>{{ formatPct(result.mutualFund.realReturnPct) }}</strong></div>
            <div class="metric"><span>Inflation-adjusted Value</span><strong>{{ formatINR(result.mutualFund.inflationAdjustedFinalValue) }}</strong></div>
            <div class="metric"><span>Liquidity</span><strong>{{ result.mutualFund.liquidity }}</strong></div>
            <div class="metric"><span>Flexibility</span><strong>{{ result.mutualFund.flexibility }}</strong></div>
            <div class="metric"><span>Risk</span><strong>{{ result.mutualFund.risk }}</strong></div>
          </div>
        </template>
      </Card>

      <!-- Comparison Summary -->
      <Card class="result-card result-summary">
        <template #title>
          <h3>Comparison Summary</h3>
        </template>
        <template #content>
          <div class="summary-hero">
            <div class="summary-label">Difference in Final Value</div>
            <div class="summary-value" :class="result.differenceInFinalValue >= 0 ? 'positive' : 'negative'">
              {{ formatINR(result.differenceInFinalValue) }}
            </div>
            <small>Positive means the Mutual Fund projection is higher than the LIC total policy value.</small>
          </div>

          <div class="metric">
            <span>Inflation-adjusted Value Gap</span>
            <strong :class="result.differenceInInflationAdjustedValue >= 0 ? 'positive' : 'negative'">
              {{ formatINR(result.differenceInInflationAdjustedValue) }}
            </strong>
          </div>

          <div class="key-params">
            <h4>LIC Parameters Used</h4>
            <ul>
              <li>Plan: {{ currentPlan.label }}</li>
              <li>Term band: {{ result.lic.termBand?.label || '-' }}</li>
              <li>Annual bonus mode: {{ formData.lic.annualBonusMode }}</li>
              <li>FAB mode: {{ formData.lic.fabMode }}</li>
              <li>Paid-up assumed: {{ formData.lic.isPaidUp ? 'Yes' : 'No' }}</li>
            </ul>
          </div>
        </template>
      </Card>
    </div>

    <!-- Chart -->
    <Card v-if="result && chartData.labels" class="chart-card">
      <template #title>
        <h3>LIC vs Mutual Fund Visualization</h3>
      </template>
      <template #content>
        <div class="chart-container">
          <Chart type="bar" :data="chartData" :options="chartOptions" class="chart" />
        </div>
      </template>
    </Card>

    <!-- Section Divider -->
    <div v-if="result && result.deathComparison" class="section-divider" role="separator" aria-hidden="true"></div>

    <!-- Early Death Protection Comparison -->
    <Card v-if="result && result.deathComparison" class="death-protection-card">
      <template #title>
        <h3>🛡️ What Happens If Something Happens Early?</h3>
      </template>
      <template #content>
        <!-- Strong headline -->
        <div class="protection-hero">
          <div class="protection-hero-label">Protection Gap in Early Years</div>
          <div class="protection-hero-value">{{ formatINR(result.deathComparison.earlyProtectionGap) }}</div>
          <small>
            Extra amount your family could receive from LIC vs Mutual Fund if something happens in Year 3.
          </small>
        </div>

        <!-- Advantage tags -->
        <div class="protection-tags">
          <span v-if="result.deathComparison.licAdvantageEarly" class="protection-tag tag-lic">
            ✅ LIC advantage in early years
          </span>
          <span v-if="result.deathComparison.mfAdvantageLater" class="protection-tag tag-mf">
            📈 MF advantage in long term
          </span>
        </div>

        <!-- Scenario table -->
        <div class="protection-table">
          <div class="table-header">
            <span>Scenario</span>
            <span>Mutual Fund</span>
            <span>LIC</span>
            <span>Protection Gap</span>
          </div>
          <div
            v-for="scenario in result.deathComparison.scenarios"
            :key="scenario.year"
            class="table-row"
          >
            <span class="scenario-label">{{ scenario.label }}</span>
            <span>{{ formatINR(scenario.mfValue) }}</span>
            <span>{{ formatINR(scenario.licValue) }}</span>
            <span :class="scenario.gap >= 0 ? 'gap-lic' : 'gap-mf'">
              {{ scenario.gap >= 0 ? '+' : '' }}{{ formatINR(scenario.gap) }}
            </span>
          </div>
        </div>

        <!-- Impactful narrative -->
        <div class="protection-narrative">
          <h4>Early Protection Matters More Than Returns</h4>
          <p>In the early years of your financial journey, the biggest risk is not low returns — it is loss of income.</p>
          <p>A Mutual Fund grows slowly in the beginning, so in the initial years, your family would receive only the accumulated investment.</p>
          <p>A LIC policy, however, provides the full sum assured (or multiple of premium) from day one, ensuring strong financial protection even if something happens early.</p>
          <p>As time passes and investments grow, the difference reduces and wealth creation becomes more important.</p>
          <p>This is why protection and wealth should be seen as two different goals.</p>
        </div>
      </template>
    </Card>

    <!-- Section Divider -->
    <div v-if="result && result.deathComparison" class="section-divider" role="separator" aria-hidden="true"></div>

    <!-- Survival Benefit Schedule -->
    <Card v-if="result && result.lic.survivalBenefits.items.length" class="schedule-card">
      <template #title>
        <h3>Survival Benefit Schedule</h3>
      </template>
      <template #content>
        <div class="schedule-table">
          <div class="table-header">
            <span>Year</span>
            <span>% of SA</span>
            <span>Amount</span>
          </div>
          <div
            v-for="item in result.lic.survivalBenefits.items"
            :key="item.year"
            class="table-row"
          >
            <span>{{ item.year }}</span>
            <span>{{ item.pctOfSa }}%</span>
            <span class="highlight-value">{{ formatINR(item.amount) }}</span>
          </div>
        </div>
      </template>
    </Card>

    <!-- Advisor Notes -->
    <Card v-if="result" class="notes-card">
      <template #title>
        <h3>📌 Advisor Notes</h3>
      </template>
      <template #content>
        <div class="notes-grid">
          <div class="note-item">
            <h4>Comparison Warnings</h4>
            <ul>
              <li v-for="(msg, idx) in result.messages.comparisonWarnings" :key="`warn-${idx}`">{{ msg }}</li>
            </ul>
          </div>
          <div class="note-item">
            <h4>LIC Notes</h4>
            <ul>
              <li v-for="(msg, idx) in result.messages.licGeneral" :key="`lic-${idx}`">{{ msg }}</li>
            </ul>
          </div>
          <div class="note-item">
            <h4>Mutual Fund Notes</h4>
            <ul>
              <li v-for="(msg, idx) in result.messages.mutualFundGeneral" :key="`mf-${idx}`">{{ msg }}</li>
            </ul>
          </div>
        </div>
      </template>
    </Card>

    <!-- Section Divider -->
    <div class="section-divider" role="separator" aria-hidden="true"></div>

    <!-- Term Insurance Reference Table -->
    <Card class="term-card">
      <template #title>
        <div class="term-card-title">
          <span class="term-title-icon">🛡️</span>
          <div>
            <h3>ICICI Pru Term Insurance — ₹1 Crore Cover</h3>
            <p class="term-subtitle">Indicative premiums for a non-smoker male in good health (coverage till age 60)</p>
          </div>
        </div>
      </template>
      <template #content>
        <!-- Hero callout -->
        <div class="term-hero">
          <div class="term-hero-stat">
            <span class="term-hero-value">₹800/mo</span>
            <span class="term-hero-label">as low as at age 25</span>
          </div>
          <div class="term-hero-divider"></div>
          <div class="term-hero-text">
            <p>A pure term plan costs a fraction of an endowment premium yet delivers <strong>10–20× the life cover</strong>. Pair it with a mutual fund for the best of both worlds.</p>
          </div>
        </div>

        <!-- Table -->
        <div class="term-table-wrap">
          <div class="term-table">
            <div class="term-table-header">
              <span>Entry Age</span>
              <span>Policy Duration</span>
              <span>Cover Till</span>
              <span>Annual Premium</span>
              <span>Monthly Approx.</span>
            </div>
            <div class="term-table-row age-25">
              <span class="term-age-badge">25 yrs</span>
              <span>35 years</span>
              <span class="term-cover-age">Age 60</span>
              <span class="term-premium">₹9,500 – ₹10,500</span>
              <span class="term-monthly">~₹800–900</span>
            </div>
            <div class="term-table-row age-30">
              <span class="term-age-badge">30 yrs</span>
              <span>30 years</span>
              <span class="term-cover-age">Age 60</span>
              <span class="term-premium">₹11,000 – ₹12,000</span>
              <span class="term-monthly">~₹900–1,000</span>
            </div>
            <div class="term-table-row age-35">
              <span class="term-age-badge">35 yrs</span>
              <span>25 years</span>
              <span class="term-cover-age">Age 60</span>
              <span class="term-premium">₹13,000 – ₹15,000</span>
              <span class="term-monthly">~₹1,100–1,300</span>
            </div>
            <div class="term-table-row age-40">
              <span class="term-age-badge">40 yrs</span>
              <span>20 years</span>
              <span class="term-cover-age">Age 60</span>
              <span class="term-premium">₹18,000 – ₹22,000</span>
              <span class="term-monthly">~₹1,500–1,800</span>
            </div>
            <div class="term-table-row age-45">
              <span class="term-age-badge">45 yrs</span>
              <span>15 years</span>
              <span class="term-cover-age">Age 60</span>
              <span class="term-premium">₹28,000 – ₹35,000</span>
              <span class="term-monthly">~₹2,300–3,000</span>
            </div>
          </div>
        </div>

        <!-- Key insight -->
        <div class="term-insight">
          <i class="pi pi-lightbulb term-insight-icon"></i>
          <p><strong>Buy early, pay less.</strong> Waiting just 5 years from age 30 to 35 increases the annual premium by ~₹3,000 for the same ₹1 Cr cover. Locking in a term plan young is one of the best financial decisions you can make.</p>
        </div>

        <!-- Disclaimer chips -->
        <div class="term-disclaimer">
          <div class="term-disclaimer-title">⚠️ Premiums vary based on</div>
          <div class="term-disclaimer-chips">
            <span class="term-chip">🚬 Smoking status</span>
            <span class="term-chip">🏥 Medical history</span>
            <span class="term-chip">➕ Riders (critical illness, accidental death, etc.)</span>
            <span class="term-chip">📋 Exact policy variant (iProtect Smart / iProtect Smart Plus)</span>
          </div>
          <p class="term-disclaimer-note">Source: ICICI Prudential Life Insurance indicative rates. Verify on the insurer's website or with your advisor before purchasing.</p>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.mf-lic-comparison-calculator {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.calculator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-title {
  font-weight: 700;
  color: var(--an-primary);
  font-size: 16px;
  margin-top: 8px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--an-primary);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 14px;
}

.info-icon {
  cursor: help;
  font-size: 12px;
  opacity: 0.7;
  margin-left: 4px;
  transition: opacity 0.2s;
}

.info-icon:hover {
  opacity: 1;
}

.help-text {
  color: var(--an-text-secondary);
  font-size: 12px;
  font-style: italic;
}

.input-field {
  width: 100%;
}

.calculate-btn {
  width: 100%;
  margin-top: 16px;
}

/* Results */
.results-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 24px;
}

.result-card {
  height: 100%;
}

.result-lic {
  border-top: 4px solid #0f766e;
}

.result-mf {
  border-top: 4px solid #2563eb;
}

.result-summary {
  border-top: 4px solid #7c3aed;
}

.metrics {
  display: flex;
  flex-direction: column;
}

.metric {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px dashed var(--an-gray3);
  font-size: 14px;
}

.metric:last-child {
  border-bottom: 0;
}

.metric span {
  color: var(--an-text-secondary);
}

.metric strong {
  text-align: right;
  color: var(--an-text-primary);
}

.metric.highlight {
  background: var(--an-background-light);
  border-radius: 8px;
  padding: 12px;
  margin: 6px 0;
  font-size: 15px;
}

.metric.highlight span,
.metric.highlight strong {
  color: var(--an-primary);
  font-weight: 700;
}

.positive {
  color: var(--an-success);
  font-weight: 700;
}

.negative {
  color: var(--an-error);
  font-weight: 700;
}

.summary-hero {
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  text-align: center;
}

.summary-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 6px;
}

.summary-value {
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.summary-value.positive,
.summary-value.negative {
  color: var(--an-white);
}

.summary-hero small {
  font-size: 12px;
  opacity: 0.85;
}

.key-params {
  margin-top: 16px;
}

.key-params h4 {
  margin: 0 0 8px 0;
  color: var(--an-text-primary);
  font-size: 15px;
}

.key-params ul {
  margin: 0;
  padding-left: 18px;
  color: var(--an-text-secondary);
  font-size: 13px;
  line-height: 1.7;
}

/* Section Divider */
.section-divider {
  height: 2px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(90deg, transparent, var(--an-primary), transparent);
  opacity: 0.4;
  margin: 4px 0;
}

/* Early Death Protection */
.death-protection-card {
  border-top: 4px solid #b91c1c;
}

.protection-hero {
  background: linear-gradient(135deg, #b91c1c, #7c3aed);
  color: var(--an-white);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  text-align: center;
}

.protection-hero-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 6px;
}

.protection-hero-value {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.protection-hero small {
  font-size: 12px;
  opacity: 0.9;
}

.protection-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.protection-tag {
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
}

.tag-lic {
  background: rgba(15, 118, 110, 0.12);
  color: #0f766e;
}

.tag-mf {
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
}

.protection-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.protection-table .table-header,
.protection-table .table-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr;
  gap: 16px;
  padding: 12px;
  align-items: center;
}

.protection-table .table-header {
  font-weight: 700;
  background: var(--an-background-light);
  border-radius: 6px;
  color: var(--an-text-primary);
}

.protection-table .table-row {
  border-bottom: 1px solid var(--an-gray3);
  font-size: 14px;
}

.protection-table .table-row:hover {
  background: var(--an-background-light);
}

.protection-table .scenario-label {
  font-weight: 600;
  color: var(--an-text-primary);
}

.gap-lic {
  color: #0f766e;
  font-weight: 700;
}

.gap-mf {
  color: #2563eb;
  font-weight: 700;
}

.protection-narrative {
  margin-top: 20px;
  padding: 20px;
  border-radius: 8px;
  background: var(--an-background-light);
  border-left: 4px solid #b91c1c;
}

.protection-narrative h4 {
  margin: 0 0 12px 0;
  color: var(--an-text-primary);
  font-size: 16px;
}

.protection-narrative p {
  margin: 0 0 10px 0;
  color: var(--an-text-secondary);
  line-height: 1.6;
  font-size: 14px;
}

.protection-narrative p:last-child {
  margin-bottom: 0;
}

/* Chart */
.chart-container {
  height: 400px;
  width: 100%;
}

.chart {
  height: 100% !important;
  width: 100% !important;
}

/* Schedule table */
.schedule-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  padding: 12px;
  align-items: center;
}

.table-header {
  font-weight: 700;
  background: var(--an-background-light);
  border-radius: 6px;
  color: var(--an-text-primary);
}

.table-row {
  border-bottom: 1px solid var(--an-gray3);
  font-size: 14px;
}

.table-row:hover {
  background: var(--an-background-light);
}

.highlight-value {
  font-weight: 700;
  color: var(--an-primary);
}

/* Notes */
.notes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.note-item {
  padding: 16px;
  border-radius: 8px;
  background: var(--an-background-light);
  border-left: 4px solid var(--an-primary);
}

.note-item h4 {
  margin: 0 0 12px 0;
  color: var(--an-text-primary);
  font-size: 15px;
}

.note-item ul {
  margin: 0;
  padding-left: 18px;
  color: var(--an-text-secondary);
  line-height: 1.6;
  font-size: 13px;
}

.note-item li {
  margin-bottom: 8px;
}

/* ── Term Insurance Reference Card ── */
.term-card {
  border-top: 4px solid #0ea5e9;
}

.term-card-title {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.term-title-icon {
  font-size: 1.6rem;
  line-height: 1;
  margin-top: 2px;
}

.term-card-title h3 {
  margin: 0 0 4px 0;
  color: var(--an-text-primary);
  font-size: 17px;
}

.term-subtitle {
  margin: 0;
  font-size: 12px;
  color: var(--an-text-secondary);
  font-weight: 400;
}

/* Hero callout */
.term-hero {
  display: flex;
  align-items: center;
  gap: 20px;
  background: linear-gradient(135deg, #0c4a6e, #0ea5e9);
  color: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.term-hero-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 110px;
}

.term-hero-value {
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
}

.term-hero-label {
  font-size: 12px;
  opacity: 0.85;
  margin-top: 4px;
  text-align: center;
}

.term-hero-divider {
  width: 1px;
  height: 48px;
  background: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.term-hero-text {
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.95;
  min-width: 200px;
}

.term-hero-text strong {
  color: #fff;
}

/* Table */
.term-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 16px;
  border-radius: 10px;
  border: 1px solid var(--an-gray3);
}

.term-table {
  min-width: 560px;
  width: 100%;
}

.term-table-header {
  display: grid;
  grid-template-columns: 100px 1fr 90px 1.4fr 1fr;
  gap: 12px;
  padding: 12px 16px;
  background: var(--an-background-light);
  font-weight: 700;
  font-size: 13px;
  color: var(--an-text-primary);
  border-bottom: 2px solid var(--an-gray3);
}

.term-table-row {
  display: grid;
  grid-template-columns: 100px 1fr 90px 1.4fr 1fr;
  gap: 12px;
  padding: 13px 16px;
  align-items: center;
  font-size: 14px;
  border-bottom: 1px solid var(--an-gray3);
  transition: background 0.15s;
}

.term-table-row:last-child {
  border-bottom: none;
}

.term-table-row:hover {
  background: var(--an-background-light);
}

/* Age accent strips */
.term-table-row.age-25 { border-left: 4px solid #22c55e; }
.term-table-row.age-30 { border-left: 4px solid #84cc16; }
.term-table-row.age-35 { border-left: 4px solid #eab308; }
.term-table-row.age-40 { border-left: 4px solid #f97316; }
.term-table-row.age-45 { border-left: 4px solid #ef4444; }

.term-age-badge {
  display: inline-block;
  font-weight: 700;
  font-size: 13px;
  color: var(--an-text-primary);
}

.term-cover-age {
  color: var(--an-text-secondary);
  font-size: 13px;
}

.term-premium {
  font-weight: 700;
  color: var(--an-text-primary);
}

.term-monthly {
  color: #0ea5e9;
  font-weight: 600;
  font-size: 13px;
}

/* Key insight */
.term-insight {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(14, 165, 233, 0.08);
  border: 1px solid rgba(14, 165, 233, 0.25);
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 16px;
}

.term-insight-icon {
  font-size: 1.2rem;
  color: #0ea5e9;
  margin-top: 2px;
  flex-shrink: 0;
}

.term-insight p {
  margin: 0;
  font-size: 14px;
  color: var(--an-text-secondary);
  line-height: 1.6;
}

.term-insight strong {
  color: var(--an-text-primary);
}

/* Disclaimer */
.term-disclaimer {
  background: var(--an-background-light);
  border-radius: 10px;
  padding: 16px 18px;
  border: 1px dashed var(--an-gray3);
}

.term-disclaimer-title {
  font-weight: 700;
  font-size: 13px;
  color: var(--an-text-primary);
  margin-bottom: 10px;
}

.term-disclaimer-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.term-chip {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 999px;
  background: var(--an-white, #fff);
  border: 1px solid var(--an-gray3);
  font-size: 12px;
  color: var(--an-text-secondary);
  font-weight: 500;
}

.term-disclaimer-note {
  margin: 0;
  font-size: 11px;
  color: var(--an-text-secondary);
  font-style: italic;
  opacity: 0.8;
}

/* ── Responsive Design ── */
@media (max-width: 1024px) {
  .results-grid {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .result-summary {
    grid-column: 1 / -1;
  }
}

@media (max-width: 768px) {
  .mf-lic-comparison-calculator {
    gap: 16px;
  }

  .calculator-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .results-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .result-summary {
    grid-column: auto;
  }

  .chart-container {
    height: 280px;
  }

  .notes-grid {
    grid-template-columns: 1fr;
  }

  .summary-value {
    font-size: 1.3rem;
  }

  .protection-hero-value {
    font-size: 1.6rem;
  }

  /* Make protection table scrollable on small screens */
  .protection-table {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .protection-table .table-header,
  .protection-table .table-row {
    min-width: 480px;
    gap: 10px;
    padding: 10px 8px;
    font-size: 13px;
  }

  .protection-tags {
    flex-direction: column;
    gap: 8px;
  }

  .protection-narrative {
    padding: 14px;
    margin-top: 14px;
  }

  .key-params ul {
    font-size: 12px;
  }

  .term-hero {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
  }

  .term-hero-divider {
    display: none;
  }

  .term-hero-stat {
    align-items: flex-start;
  }

  .term-hero-value {
    font-size: 1.6rem;
  }
}

@media (max-width: 480px) {
  .mf-lic-comparison-calculator {
    gap: 12px;
  }

  .form-fields {
    gap: 14px;
  }

  .section-title {
    font-size: 14px;
  }

  .field label {
    font-size: 13px;
  }

  .metric {
    font-size: 12px;
    padding: 8px 0;
    flex-wrap: wrap;
    gap: 4px;
  }

  .metric strong {
    text-align: left;
  }

  .metric.highlight {
    font-size: 13px;
    padding: 10px;
  }

  .summary-hero {
    padding: 14px;
  }

  .summary-label {
    font-size: 13px;
  }

  .summary-value {
    font-size: 1.15rem;
  }

  .protection-hero {
    padding: 16px;
  }

  .protection-hero-value {
    font-size: 1.4rem;
  }

  .protection-hero-label {
    font-size: 13px;
  }

  .protection-narrative {
    padding: 12px;
  }

  .protection-narrative h4 {
    font-size: 14px;
  }

  .protection-narrative p {
    font-size: 13px;
  }

  .note-item {
    padding: 12px;
  }

  .note-item h4 {
    font-size: 14px;
  }

  .chart-container {
    height: 220px;
  }

  .calculate-btn {
    margin-top: 8px;
  }
}
</style>
